import React, { useLayoutEffect, useState } from "react";
import ReactGA from "react-ga4";
import { useNavigate, useLocation } from "react-router-dom";

import * as yup from "yup";
import { useFormik } from "formik";

import { Input } from "design_system/src";

import { store } from "store";
import { logout } from "store/actions/user";
import {
    fetchChangePassword,
    fetchOtpPassword,
    fetchOtpValidation
} from "models/auth-client/controllers/auth.controller";
import { PasswordInput } from "components/passwordInput/PasswordInput";
import { PasswordErrorIndicator } from "components/passwordInput/PasswordErrorIndicator";
import { AuthCard } from "../AuthCard";

const passwordRegex =
    /(?:(?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/;

const otpValidationSchema = yup.object({
    otp: yup
        .string()
        .matches(/^\d+$/, "Numero invalido")
        .required("Campo requerido"),
});

const passwordValidationSchema = yup.object({
    password: yup
        .string()
        .required("Campo requerido")
        .min(6, "Mínimo 6 caracteres")
        .max(50, "Máximo 50 caracteres")
        .matches(passwordRegex, "La contraseña debe tener al menos un letra en mayúscula, una en minúscula y un número"),
    passwordConfirmation: yup
        .string()
        .required("Campo requerido")
        .test(
            "",
            "Las contraseñas deben ser iguales",
            function (value) {
                return value === this.parent.password;
            }
        ),
});

interface AuthValues {
    curp?: string;
    email?: string;
    otpSended?: boolean;
    otpValid?: boolean;
    successfulActivation?: boolean;
}

export const ActivateMiCuentaScreen = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useLayoutEffect(() => {
        if (!state?.email) {
            navigate("/ingresar");
        }
        ReactGA.event("VIEW_MyAccount_activate_account", {
            category: "VIEW_MyAccount_activate_account",
            label: "page view to activate account",
        });
    }, []);

    const resetChanges = () => {
        navigate("/ingresar", {state:{}});
    };

    const sendOTP = (curp: string) => {
        setLoading(true);
        fetchOtpPassword(curp)
            .then(async () => {
                setLoading(false);
                ReactGA.event("VIEW_MyAccount_activate_code", {
                    category: "VIEW_MyAccount_activate_code",
                    label: "page view to write code to activate account",
                });
                navigate("/activar-cuenta", {state:{ ...state, otpSended: true, otpValid: false }});
            })
            .catch((e: any) => {
                setLoading(false);
            });
    };

    const otpFormik = useFormik({
        initialValues: {
            otp: "",
        },
        validationSchema: otpValidationSchema,
        onSubmit: ({ otp }) => {
            if (state?.curp) {
                setLoading(true);
                fetchOtpValidation(state?.curp, `${otp}`)
                    .then(async () => {
                        setLoading(false);
                        ReactGA.event("VIEW_MyAccount_new_password", {
                            category: "VIEW_MyAccount_new_password",
                            label: "page view to set new password to access account",
                        });
                        navigate("/activar-cuenta", {state:{ ...state, otpValid: true }});
                    })
                    .catch((e: any) => {
                        setLoading(false);
                        if (e.message === "OTP not valid") {
                            setErrorMessage("Código inválido");
                        } else if(e.message === "OTP expired") {
                            setErrorMessage("El código ha expirado");
                        }
                    });
            }
        },
    });

    const passwordFormik = useFormik({
        initialValues: {
            password: "",
            passwordConfirmation: "",
        },
        validationSchema: passwordValidationSchema,
        onSubmit: ({ password, passwordConfirmation }) => {
            setLoading(true);
            fetchChangePassword(password, passwordConfirmation)
                .then(async () => {
                    setLoading(false);
                    store.dispatch(logout());
                    ReactGA.event("VIEW_MyAccount_account_activated", {
                        category: "VIEW_MyAccount_account_activated",
                        label: "page view with account activated message",
                    });
                    navigate("/activar-cuenta", {state:{ ...state, successfulActivation: true }});
                })
                .catch((e: any) => {
                    setLoading(false);
                    setErrorMessage("No fue posible la activación de la cuenta, el token ha expirado");
                });
        },
    });

    return <div className="dso_container" style={{ minHeight: "75vh" }}>
        <div className="m_y_lg_mobile m_y_xxxl_desktop m_x_auto w_600_px_desktop">
            {(state?.curp && state?.email && !state?.otpSended && !state?.otpValid) && <AuthCard
                title="Activación de la cuenta"
                subtitle={<p>Enviaremos un mensaje a tu correo electrónico: {state?.email} con el código de activación</p>}
                buttonTitle="Enviar código"
                buttonAction={() => sendOTP(state?.curp ?? "")}
                loading={loading}
                link={{
                    title: "",
                    buttonTitle: "Cancelar",
                    buttonAction: () => resetChanges()
                }}
            />}

            {(state?.curp && state?.email && state?.otpSended && !state?.otpValid) && <AuthCard
                title="Activación de la cuenta"
                subtitle={<p>Ingresa el código enviado a tu correo electrónico. Por favor, verifica tu carpeta de spam si no has recibido el correo en tu bandeja de entrada.</p>}
                formik={otpFormik}
                form={<Input
                    title=""
                    name="otp"
                    type="text"
                    placeholder="Ingresa el código"
                    onBlur={otpFormik.handleBlur}
                    value={otpFormik.values.otp}
                    onChange={(e) => {
                        setErrorMessage(undefined);
                        otpFormik.handleChange(e);
                    }}
                    error={!!otpFormik.errors.otp && !!otpFormik.touched.otp}
                    subtitle={otpFormik.touched.otp ? otpFormik.errors.otp : ""}
                />}
                errorMessage={errorMessage}
                buttonTitle="Confirmar código"
                buttonAction={() => {}}
                loading={loading}
                link={{
                    title: "",
                    buttonTitle: "Reenviar código",
                    buttonAction: () => {
                        setErrorMessage(undefined);
                        otpFormik.setFieldValue("otp", "");
                        sendOTP(state.curp ?? "");
                    }
                }}
            />}

            {(state?.otpValid && !state?.successfulActivation) && <AuthCard
                title="Crea tu contraseña"
                subtitle={<p>Ingresa tu contraseña</p>}
                loading={loading}
                formik={passwordFormik}
                errorMessage={errorMessage}
                form={
                    <div className="m_b_md">
                        <PasswordInput
                            title=""
                            name="password"
                            placeholder="Ingresa tu contraseña"
                            onBlur={passwordFormik.handleBlur}
                            value={passwordFormik.values.password}
                            onChange={passwordFormik.handleChange}
                            error={!!passwordFormik.errors.password && !!passwordFormik.touched.password}
                            subtitle={passwordFormik.touched.password ? passwordFormik.errors.password : ""}
                            showErrors={false}
                        />
                        <PasswordInput
                            title=""
                            name="passwordConfirmation"
                            placeholder="Confirma tu contraseña"
                            onBlur={passwordFormik.handleBlur}
                            value={passwordFormik.values.passwordConfirmation}
                            onChange={passwordFormik.handleChange}
                            error={!!passwordFormik.errors.passwordConfirmation && !!passwordFormik.touched.passwordConfirmation}
                            subtitle={passwordFormik.touched.passwordConfirmation ? passwordFormik.errors.passwordConfirmation : ""}
                        />
                        <PasswordErrorIndicator value={passwordFormik.values.password} />
                    </div>
                }
                buttonTitle="Entrar"
                buttonAction={() => {}}
                link={errorMessage ? {
                    title: "",
                    buttonTitle: "Reenviar código",
                    buttonAction: () => {
                        setErrorMessage(undefined);
                        sendOTP(state.curp ?? "");
                        otpFormik.setFieldValue("otp", "");
                        passwordFormik.setFieldValue("password", "");
                        passwordFormik.setFieldValue("passwordConfirmation", "");
                    }
                }: undefined}
            />}

            {(state?.otpValid && state?.successfulActivation) && <AuthCard
                title="Cuenta  activada"
                subtitle={<p>La cuenta asociada al CURP {state?.curp} ha sido activada.</p>}
                buttonTitle="Iniciar sesión"
                buttonAction={() => navigate("/ingresar")}
            />}
        </div>
    </div>;
};
